// eslint-disable-next-line
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../../services/container/root';
import PagedDataStore, { IFetchListArgs } from '../../../stores/abstract-paged-data-store';
import SubscriptionPlanEditorStore, { IPlanEditorModelChanges, PLAN_CHARGE_TYPES } from './subscription-plan-editor-store';
import SubscriptionPlansService from '../../../services/api/subscription-plans-service';
import type { IPlanEditorModel, IPlanFilters } from '../../../models/Plan';
import { PLAN_TYPES } from '../../../models/Plan';

class SubscriptionPlanPagedListStore extends PagedDataStore<SubscriptionPlanEditorStore>  {
	private _type?: PLAN_TYPES;
	constructor(
		private _combinedPagedListDataActualiser: (savedPlan: SubscriptionPlanEditorStore, storeType: PLAN_TYPES) => void,
		filters: IPlanFilters
	) {
		super();
		this._type = filters.type;
		makeObservable(this);
	}
	readonly perPage: number = 20;
	@observable private _selectedPlan?: SubscriptionPlanEditorStore;

	protected get service(): SubscriptionPlansService {
		return rootContainer.get<SubscriptionPlansService>(TYPE.SubscriptionPlansService);
	}

	@computed public get selectedPlan(): SubscriptionPlanEditorStore | undefined {
		return this._selectedPlan;
	}

	@action
	public dropSelectedPlan() {
		this._selectedPlan = undefined;
	}

	@action
	public async setNewPlan(copyFromId?: number) {
		if (!copyFromId) {
			const isCustom = this._type === PLAN_TYPES.CUSTOM;
			const modelChanges: IPlanEditorModelChanges = { is_custom: isCustom, type: PLAN_CHARGE_TYPES.RECURRING };
			this._selectedPlan = new SubscriptionPlanEditorStore(this._actualiseData.bind(this));
			this._selectedPlan.setChanges(modelChanges);
			return;
		}
		this._isLoading = true;
		const plan = this.list.find(i => i.id === copyFromId);
		let copyFromModel = plan?.model;

		if (!copyFromModel) {
			copyFromModel = await this.service.fetchPlan(copyFromId);
		}
		runInAction(() => {
			this._selectedPlan = new SubscriptionPlanEditorStore(this._actualiseData.bind(this));
			if (copyFromModel) {
				/** when copying from existing plan, to prevent from the new plan to be saved as public - set to custom by default */
				copyFromModel = { ...copyFromModel, is_custom: true, name: `${copyFromModel.name} - Copy` };
				this._selectedPlan.setChanges(copyFromModel);
			}
			this._isLoading = false;
		});
	}

	@action
	public async setEditPlan(id: number) {
		this._selectedPlan = this.list.find(i => i.id === id);
		if (!this._selectedPlan) {
			this._isLoading = true;
			const res = await this.service.fetchPlan(id);
			runInAction(() => {
				this._selectedPlan = new SubscriptionPlanEditorStore(this._actualiseData.bind(this), res);
				this._isLoading = false;
			});
		}
	}

	@action
	public addToList(planModel: IPlanEditorModel): SubscriptionPlanEditorStore {
		const planToAdd = new SubscriptionPlanEditorStore(this._actualiseData.bind(this), planModel);
		this._list = [planToAdd, ...this._list];
		this._actualiseOnInstallInList(planToAdd);
		this._total++;
		return planToAdd;
	}

	@action
	public removeFromList(id: number) {
		const newList = this._list.filter(p => p.id !== id);
		if (newList.length === this._list.length - 1) {
			this._list = newList;
			this._total--;
		}
	}

	protected async fetchList(args: IFetchListArgs) {
		const result = await this.service.fetchList({ ...args, type: this._type });
		return {
			...result,
			data: result.data.map(plan => new SubscriptionPlanEditorStore(this._actualiseData.bind(this), plan))
		};
	}

	@action
	private _actualiseData(savedPlanModel: IPlanEditorModel) {		
		let savedPlan = this._list.find(p => p.id === savedPlanModel.id);
		if (savedPlan) {
			this._actualiseOnInstallInList(savedPlan);
		} else {
			// if the plan is not found in the list means that is a new plan, need to add to the list
			savedPlan = this.addToList(savedPlanModel);
		}

		if (savedPlan && this._type) {
			this._combinedPagedListDataActualiser(savedPlan, this._type);
		}
	}

	@action
	private _actualiseOnInstallInList(planStore: SubscriptionPlanEditorStore) {
		if (!planStore.on_install) {
			return;
		}
		this._list.forEach((p, i) => {
			if (p.id !== planStore.id && p.on_install) {
				const newModel = { ...p.model, on_install: false };
				this._list[i] = new SubscriptionPlanEditorStore(this._actualiseData.bind(this), newModel);
			}
		});
	}
}

export default SubscriptionPlanPagedListStore;
